<template>
  <el-dialog
    v-model="dialogVisible"
    width="60%"
    destroy-on-close
  >
    <Container
      :title="t('pay.slipPayment')"
    >
      <template #main>
        <h3>{{ t('pay.toBeSlipPayment') }}</h3>
        <SimpleTable
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :hadOperate="false"
          :cellStyle="cellStyleFun"
        />
        <el-row style="padding-top: 2rem; padding-bottom: 1rem;">
          <el-col :span="3">
            <h3>{{ t('pay.slipAmount') }}</h3>
          </el-col>
          <el-col :span="5">
            <el-input
              v-model="amount"
              type="text"
            />
            <div
              v-if="cannotBeSplit"
              style="color: #FF4949; margin-top: 0.5rem"
            >
              {{ t('pay.slipAmountP') }}
            </div>
          </el-col>
          <el-col :span="16">
            <el-button
              style="margin-left: 2rem;"
              class="order-button"
              @click="handleSlip"
            >
              {{ t('pay.preView') }}
            </el-button>
          </el-col>
          <span style="color: #FF4949;">{{ t('pay.slipAmountTip') }}</span>
        </el-row>
        <h3>{{ t('pay.afterSlipPayment') }}</h3>
        <SimpleTable
          :listLoading="listLoading"
          :tableData="tableDataSub"
          :columnStruct="subColumnStruct"
          :hadOperate="false"
        />
        <el-form
          ref="cFormRef"
          label-position="left"
          style="margin-left:35%; font-size: 14px;"
        >
          <el-row :gutter="5">
            <el-col :span="24">
              <el-form-item
                label-width="50%"
                style="text-align: right; margin-top: 15px;"
              >
                <el-button
                  ref="opsButton"
                  class="order-button"
                  @click="handleCancel"
                >
                  {{ t('pay.cancel') }}
                </el-button>
                <el-button
                  ref="opsButton"
                  class="order-button"
                  type="primary"
                  @click="handleConfirm"
                >
                  {{ t('pay.confirm') }}
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </template>
    </Container>
  </el-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import Decimal from 'decimal.js'
import { formatDate, deepClone, formatDecimal, formatThousands } from '@/utils'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import { splitPaymentSlipWork } from '@/apis/paymentSlip/paymentSlip'
import { PaymentSlipWorkModel, PaymentSlipWorkSubModel, SlipWorkSplit } from '@/model/paymentSlip/paymentSlipModel'

export default defineComponent({
  name: 'PaymentSlipWorkDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    dataId: {
      type: Number,
      required: true
    },
    isSub: {
      type: Boolean,
      required: true
    },
    workData: {
      type: Object,
      default: null
    }
  },
  components: { SimpleTable, Container },
  emits: ['update:modelValue', 'refresh'],
  setup(props, context) {
    const { t } = useI18n()
    // 使用 computed 实现双向绑定
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const paymentSlipTableStruct = [
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'bankName', label: t('pay.ReceivingBank') },
      { prop: 'amount', label: t('pay.PaymentAmount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'dueDate', label: t('Acceptance.dueDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'paymentDate', label: t('pay.paymentDay'), formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd') },
      { prop: 'purpose', label: t('pay.purposeView') },
      {
        prop: 'isSplitting',
        label: '',
        formatter: (row: any) => { if (row.isSplitting) return '← 待拆分项' }
      }
    ]
    const subPaymentSlipTableStruct = [
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'bankName', label: t('pay.ReceivingBank') },
      { prop: 'amount', label: t('pay.PaymentAmount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'dueDate', label: t('Acceptance.dueDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'paymentDate', label: t('pay.paymentDay'), formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd') },
      { prop: 'purpose', label: t('pay.purposeView') }
    ]
    const dataMap = reactive({
      tableData: Array<PaymentSlipWorkModel>(),
      tableDataSub: Array<PaymentSlipWorkModel>(),
      listLoading: true,
      columnStruct: paymentSlipTableStruct,
      subColumnStruct: subPaymentSlipTableStruct,
      defaultBankId: 0,
      amount: null as string | null,
      cannotBeSplit: false,
      async getCompanyBankList() {
        dataMap.listLoading = true
        dataMap.tableData = [props.workData] as Array<PaymentSlipWorkModel>
        dataMap.tableDataSub = dataMap.tableData
        if (props.isSub) {
          const work = dataMap.tableData[0].children.find(c => c.subId === props.dataId)
          if (work) {
            work.isSplitting = true
          }
        } else {
          dataMap.tableData[0].isSplitting = true
        }
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      cellStyleFun({ row, columnIndex }: { row: any, columnIndex: number }) {
        if (row.isSplitting && columnIndex === 8) {
          return {
            color: '#FF4949'
          }
        }
        return {}
      }
    })
    const handleSlip = () => {
      let amount = null as number | null
      try {
        amount = Number(dataMap.amount)
      } catch (e) {
        ElMessage({
          message: '金额格式错误',
          type: 'error',
          duration: 2000
        })
        return
      }
      if (amount == null || amount <= 0) {
        ElMessage({
          message: t('pay.slipAmountCannotBeZeroP'),
          type: 'error',
          duration: 2000
        })
        return
      }
      dataMap.tableDataSub = []
      if (props.isSub) {
        const work = Object.assign({} as PaymentSlipWorkModel, props.workData)
        work.children = deepClone(props.workData.children)
        const sub1 = work.children.find(c => c.subId === props.dataId)
        if (sub1) {
          const sub2 = Object.assign({} as PaymentSlipWorkSubModel, sub1)
          sub1.amount = new Decimal(sub1.amount).minus(new Decimal(amount)).toNumber()
          if (sub1.amount > 0) {
            dataMap.cannotBeSplit = false
          } else {
            dataMap.cannotBeSplit = true
            return
          }
          sub2.amount = amount
          work.children.push(sub2)
          dataMap.tableDataSub = [work]
        }
      } else {
        const sub1 = Object.assign({} as PaymentSlipWorkModel, props.workData)
        const sub2 = Object.assign({} as PaymentSlipWorkModel, props.workData)
        sub1.amount = new Decimal(sub1.amount).minus(new Decimal(amount)).toNumber()
        sub2.amount = amount
        if (sub1.amount > 0) {
          dataMap.cannotBeSplit = false
        } else {
          dataMap.cannotBeSplit = true
          return
        }
        dataMap.tableDataSub.push(sub1, sub2)
      }
    }
    const handleCancel = () => {
      dialogVisible.value = false
    }
    const handleConfirm = async() => {
      let amount = null
      try {
        amount = Number(dataMap.amount)
      } catch (e) {
        ElMessage({
          message: '金额格式错误',
          type: 'error',
          duration: 2000
        })
        return
      }
      if (amount == null || amount <= 0) {
        ElMessage({
          message: t('pay.slipAmountCannotBeZeroP'),
          type: 'error',
          duration: 2000
        })
        return
      }
      const work = Object.assign({} as PaymentSlipWorkModel, props.workData)
      let checkWork: PaymentSlipWorkModel | PaymentSlipWorkSubModel | undefined
      if (props.isSub) {
        checkWork = work.children.find(c => c.subId === props.dataId)
      } else {
        checkWork = work
      }
      if (!checkWork) {
        return
      } else if (amount >= checkWork.amount) {
        dataMap.cannotBeSplit = true
        dataMap.tableDataSub = []
        return
      } else {
        dataMap.cannotBeSplit = false
      }

      const param: SlipWorkSplit = {
        isSubWork: props.isSub,
        workId: props.dataId,
        workSubId: props.dataId,
        amount: amount
      }
      const res = await splitPaymentSlipWork(param)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.setSuccess'),
          type: 'success',
          duration: 2000
        })
        context.emit('refresh')
        dialogVisible.value = false
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }

    onMounted(() => {
      dataMap.getCompanyBankList()
    })
    watch(() => dataMap.amount, (value: string | null) => {
      if (value != null) {
        dataMap.amount = formatDecimal(value, 2, true)
      }
    })
    return {
      t,
      dialogVisible,
      handleSlip,
      handleCancel,
      handleConfirm,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style scoped lang="scss">
.fixed-bottom-button {
  position: fixed;
  text-align: right;
  bottom: 40px; /* 距离底部的距离 */
  right: 20px; /* 距离右边的距离 */
}
</style>
